import React from "react";
import ReactDOMServer from "react-dom/server";
import SvgIcon from "../../../../parts/SvgIcon";
import getAvatarUrl from "../../../../utils/get-avatar-url";
import { getOrganizationCoach } from "../../../../utils/user-helpers";
import { Cell } from "../Cell";
import {
  getDaysSincePill,
  getLastLoggedDays,
  getRpeCellStyle,
  getWorkoutsUrgencyClass,
  metricBaseUrl,
} from "../coachingHelpers";
import { getMonthSummary } from "../monthSummary";

export function AthleteRow({
  athlete,
  dataKey,
  theme,
  activeMonth,
  metric,
  today,
  isCurrentMonth,
  isPreviousMonth,
  unitSelected,
  activeUser,
  organizationActiveUsers,
  referAsClients,
  showWorkoutsColumn,
  showSummary,
  t,
}) {
  const { isFromOtherOrganizationMember, organizationCoachId } =
    getOrganizationCoach(activeUser, athlete._id, organizationActiveUsers);

  const lastLoggedDays = getLastLoggedDays(athlete);
  const daysSincePill = getDaysSincePill(lastLoggedDays);
  const basePath = metricBaseUrl[metric] ?? "/athlete-portal/calendar";
  const athleteReportUrl = isFromOtherOrganizationMember
    ? `${basePath}?athlete=${athlete._id}&organizationCoach=${organizationCoachId}`
    : `${basePath}?athlete=${athlete._id}`;

  return (
    <tr>
      <td className="no-left-border">
        <div className="flex gap-2 items-center">
          <a href={athleteReportUrl} className="image">
            <img
              src={getAvatarUrl(athlete, null, theme)}
              loading="lazy"
              className="profile-picture"
              width="100%"
              height="100%"
              alt={t("coachingDashboard.userProfileAlt")}
            />
          </a>
          <a href={athleteReportUrl} className="name">
            {athlete.name || athlete.email}
          </a>
        </div>
      </td>
      {showWorkoutsColumn && (
        <td
          data-tooltip-html={ReactDOMServer.renderToStaticMarkup(
            <div>
              {athlete.needsWorkoutsOn > 0
                ? t("coachingDashboard.daysUntilNewProgramRequired", {
                    count: athlete.needsWorkoutsOn,
                  })
                : t("coachingDashboard.newProgramRequired")}
            </div>
          )}
          data-html={true}
          data-tooltip-id="athlete-workoutOn"
          className="schedule-on"
        >
          <div className={getWorkoutsUrgencyClass(athlete.needsWorkoutsOn)}>
            {athlete.needsWorkoutsOn ? (
              `${athlete.needsWorkoutsOn}D`
            ) : (
              <SvgIcon
                src="/img/new-icons/warning-white.svg"
                noFilterOnLight={true}
                classNames="w-4 h-4"
              />
            )}
          </div>
        </td>
      )}
      <td className="last-logged-cell">
        {daysSincePill ? (
          <span className={`pill ${daysSincePill.tone}`}>
            {daysSincePill.label ?? t("coachingDashboard.todayColumnBadge")}
          </span>
        ) : (
          <span
            className="none"
            data-tooltip-id="athlete-last-logged-never"
            data-tooltip-content={t("coachingDashboard.lastLoggedNeverTooltip")}
          >
            {t("coachingDashboard.lastLoggedNever")}
          </span>
        )}
      </td>
      {athlete[dataKey]?.map((_, idx) => (
        <Cell
          key={`athlete-${athlete._id}-day-${idx}`}
          athlete={athlete}
          idx={idx}
          activeMonth={activeMonth}
          metric={metric}
          isCurrentMonth={isCurrentMonth}
          today={today}
          isPreviousMonth={isPreviousMonth}
          unitSelected={unitSelected}
          isFromOtherOrganizationMember={isFromOtherOrganizationMember}
          organizationCoachId={organizationCoachId}
          referAsClients={referAsClients}
          t={t}
        />
      ))}
      {showSummary && (
        <SummaryCell
          summary={getMonthSummary({
            athlete,
            metric,
            unitSelected,
          })}
          metric={metric}
          t={t}
        />
      )}
    </tr>
  );
}

function SummaryCell({ summary, metric, t }) {
  if (summary.type === "count") {
    return (
      <td className="summary-cell">
        <div className="num-cell">
          {summary.scheduled ? (
            <>
              <span className="v">
                {summary.done}/{summary.scheduled}
              </span>
              <span className="u">
                {t("coachingDashboard.summary.doneUnit")}
              </span>
            </>
          ) : (
            <span className="none">—</span>
          )}
        </div>
      </td>
    );
  }
  if (summary.type === "cycleDay") {
    return (
      <td className="summary-cell">
        <div className="num-cell">
          {summary.value ? (
            <span className="v">
              {t("coachingDashboard.summary.cycleDayValue", {
                day: summary.value,
              })}
            </span>
          ) : (
            <span className="none">—</span>
          )}
        </div>
      </td>
    );
  }
  if (summary.type === "total") {
    return (
      <td className="summary-cell">
        <div className="num-cell">
          {summary.value ? (
            <>
              <span className="v">
                {formatSummaryText(String(summary.value))}
              </span>
              <span className="u">
                {t(`coachingDashboard.summary.units.${summary.unitKey}`, {
                  count: summary.value,
                })}
              </span>
            </>
          ) : (
            <span className="none">—</span>
          )}
        </div>
      </td>
    );
  }
  if (summary.value === null) {
    return (
      <td className="summary-cell">
        <div className="num-cell">
          <span className="none">—</span>
        </div>
      </td>
    );
  }
  return (
    <td className="summary-cell">
      {metric === "sessionRPE" ? (
        <div className="rpe-avg" style={getRpeCellStyle(summary.value)}>
          <span className="val">{summary.text}</span>
          <span className="meter">
            <i
              style={{
                width: `${Math.min(Math.max(summary.value * 10, 0), 100)}%`,
              }}
            />
          </span>
          <span className="n">
            {t("coachingDashboard.summary.sessionsCount", {
              count: summary.count,
            })}
          </span>
        </div>
      ) : (
        <div className="num-cell">
          <span className="v">{formatSummaryText(summary.text)}</span>
          {summary.unit && <span className="u">{summary.unit}</span>}
          {!summary.unit && summary.unitKey && (
            <span className="u">
              {t(`coachingDashboard.summary.units.${summary.unitKey}`, {
                count: Number(summary.text.replace(/,/g, "")),
              })}
            </span>
          )}
        </div>
      )}
    </td>
  );
}

// "1828" -> "1,828"; leaves decimals ("72.5") alone.
const formatSummaryText = (text) =>
  /^\d{4,}$/.test(text) ? Number(text).toLocaleString("en-US") : text;
